import { eventEmitter } from "../../../../services";
import { clearSelection } from "../../../../lib";
import { saveEditorContents } from "../../Contents/helpers";

export const HIDE_TOOLBAR = "toolbar/hide";

export function hideToolbar() {
    eventEmitter.emit(HIDE_TOOLBAR);
    clearSelection();
}

function hideAndSave() {
    hideToolbar();
    saveEditorContents();
}

function insertEmptyDivAfter(context: string) {
    const sel = window.getSelection();
    if (!sel) {
        return;
    }

    if (sel.rangeCount) {
        const range = sel.getRangeAt(0);

        let node: Node | null = range.commonAncestorContainer;
        while (node && node.nodeName.toLowerCase() !== context) {
            node = node.parentNode;
        }

        if (node) {
            const emptyDiv = document.createElement("div");
            emptyDiv.innerHTML = "&nbsp;";
            if (node.parentNode) {
                node.parentNode.insertBefore(emptyDiv, node.nextSibling);
            }
        }
    }
}

function isEnabled(cmd: string) {
    return document.queryCommandState(cmd);
}

export const isBold = () => isEnabled("bold");
export function onBold() {
    console.log("onBold", onBold);
    document.execCommand("bold");
    hideAndSave();
}

export const isItalic = () => isEnabled("italic");
export function onItalic() {
    document.execCommand("italic");
    hideAndSave();
}

export const isUnderline = () => isEnabled("underline");
export function onUnderline() {
    document.execCommand("underline");
    hideAndSave();
}

export const isAlignRight = () => isEnabled("justifyRight");
export function onAlignRight() {
    document.execCommand("justifyRight");
    hideAndSave();
}

export const isAlignCenter = () => isEnabled("justifyCenter");
export function onAlignCenter() {
    document.execCommand("justifyCenter");
    hideAndSave();
}

export const isAlignLeft = () => isEnabled("justifyLeft");
export function onAlignLeft() {
    document.execCommand("justifyLeft");
    hideAndSave();
}

export const isAlignJustifyFull = () => isEnabled("justifyFull");
export function onAlignJustifyFull() {
    document.execCommand("justifyFull");
    hideAndSave();
}

export const isListOrdered = () => isEnabled("insertOrderedList");
export function onListOrdered() {
    document.execCommand("insertOrderedList");
    hideAndSave();
}

export const isListUnOrdered = () => isEnabled("insertUnorderedList");
export function onListUnOrdered() {
    document.execCommand("insertUnorderedList");
    hideAndSave();
}

export const isIndentIncrease = () => isEnabled("indent");
export function onIndentIncrease() {
    document.execCommand("indent");
    hideAndSave();
}

export const isIndentDecrease = () => isEnabled("outdent");
export function onIndentDecrease() {
    document.execCommand("outdent");
    hideAndSave();
}

export const isLink = () => isEnabled("createLink");
export function createLink(link: string) {
    document.execCommand("createLink", false, link);
    hideAndSave();
}

export function removeLink() {
    document.execCommand("unlink");
    hideAndSave();
}

export function insertImage(path: string) {
    document.execCommand("insertImage", false, path);
    hideAndSave();
}

export function onRemoveTextFormat() {
    document.execCommand("removeFormat");
    removeLTR();
    removeHeadings();
    hideAndSave();
}

export function onH1() {
    document.execCommand("formatBlock", false, "H1");
    // insertEmptyDivAfter('h1')
    hideAndSave();
}

export function onH2() {
    document.execCommand("formatBlock", false, "H2");
    // insertEmptyDivAfter('h2')
    hideAndSave();
}

export function onH3() {
    document.execCommand("formatBlock", false, "H3");
    // insertEmptyDivAfter('h3')
    hideAndSave();
}

export function onH4() {
    document.execCommand("formatBlock", false, "H4");
    // insertEmptyDivAfter('h4')
    hideAndSave();
}

export function onH5() {
    document.execCommand("formatBlock", false, "H5");
    // insertEmptyDivAfter('h5')
    hideAndSave();
}

export function onH6() {
    document.execCommand("formatBlock", false, "H6");
    // insertEmptyDivAfter('h6')
    hideAndSave();
}
export function onQuote() {
    document.execCommand("formatBlock", false, "BLOCKQUOTE");
    insertEmptyDivAfter("blockquote");
    hideAndSave();
}

export function onSourceCode() {
    const sel = window.getSelection();
    if (!sel) {
        return;
    }

    if (sel.rangeCount) {
        const range = sel.getRangeAt(0);

        const elmCode = document.createElement("code");
        elmCode.appendChild(range.extractContents());
        range.insertNode(elmCode);

        const parent = elmCode.parentNode;

        const emptyDiv = document.createElement("div");
        emptyDiv.innerHTML = "&nbsp;";

        if (parent && parent.parentNode) {
            parent.parentNode.insertBefore(emptyDiv, parent.nextSibling);
        }

        hideAndSave();
    }
}

export function onArabicLang() {
    applyLangOnSelection();
}

export function onRTL() {
    applyDirOnSelection("rtl");
}

export function onLTR() {
    applyDirOnSelection("ltr");
}

export function applyLangOnSelection() {
    const sel = window.getSelection();
    if (!sel) {
        return;
    }

    if (sel.rangeCount) {
        const range = sel.getRangeAt(0);

        const elmSpan = document.createElement("span");
        elmSpan.lang = "ar";
        elmSpan.appendChild(range.extractContents());
        range.insertNode(elmSpan);

        hideAndSave();
    }
}

export function applyDirOnSelection(dir: string) {
    const sel = window.getSelection();
    if (!sel) {
        return;
    }

    if (sel.rangeCount) {
        const range = sel.getRangeAt(0);

        const elmSpan = document.createElement("span");
        elmSpan.className = `${dir}-text`;
        elmSpan.appendChild(range.extractContents());
        range.insertNode(elmSpan);

        hideAndSave();
    }
}

export function removeLTR() {
    const sel = window.getSelection();
    if (!sel) {
        return;
    }

    if (sel.rangeCount) {
        const range = sel.getRangeAt(0);
        let ltrElm: Node | null = range.commonAncestorContainer;
        if (range.commonAncestorContainer.nodeName.toLowerCase() === "#text") {
            ltrElm = range.commonAncestorContainer.parentElement;
        }

        if (ltrElm) {
            const el = ltrElm as HTMLElement;
            if (el.className.includes("ltr-text")) {
                el.classList.remove("ltr-text");
            }
        }
    }
}

export function removeSourceCode() {
    const sel = window.getSelection();

    if (!sel) {
        return;
    }

    if (sel.rangeCount) {
        const range = sel.getRangeAt(0);
        let codeElm: Node | null = range.commonAncestorContainer;
        if (range.commonAncestorContainer.nodeName.toLowerCase() === "#text") {
            codeElm = range.commonAncestorContainer.parentElement;
        }

        const sourceCode = ["code"];

        if (codeElm && !sourceCode.includes(codeElm.nodeName.toLowerCase())) {
            codeElm = codeElm.parentNode;
        }

        if (
            codeElm &&
            sourceCode.includes(codeElm.nodeName.toLowerCase()) &&
            codeElm.previousSibling &&
            sourceCode.includes(codeElm.previousSibling.nodeName.toLowerCase())
        ) {
            const div = document.createElement("div");
            div.innerHTML = (codeElm as HTMLElement).innerHTML;
            const parent = (codeElm as HTMLElement).parentNode;

            if (parent) {
                parent.replaceChild(div, codeElm);
            }
        }
    }
}

export function removeHeadings() {
    const sel = window.getSelection();
    if (!sel) {
        return;
    }

    if (sel.rangeCount) {
        const range = sel.getRangeAt(0);
        let headingElm: Node | null = range.commonAncestorContainer;
        if (range.commonAncestorContainer.nodeName.toLowerCase() === "#text") {
            headingElm = range.commonAncestorContainer.parentElement;
        }

        const headings = ["h1", "h2", "h3", "h4", "h5", "h6"];

        if (
            headingElm &&
            !headings.includes(headingElm.nodeName.toLowerCase())
        ) {
            headingElm = headingElm.parentNode;
        }

        if (
            headingElm &&
            headings.includes(headingElm.nodeName.toLowerCase())
        ) {
            const div = document.createElement("div");
            div.innerHTML = (headingElm as HTMLElement).innerHTML;

            const h = headingElm.parentNode as HTMLElement;
            if (h) {
                h.replaceChild(div, headingElm);
            }
        }
    }
}

export function onEnterInContentEditable() {
    removeLTR();
    removeHeadings();
    removeSourceCode();
}
